'use client';

/**
 * Box pattern systems supported by the app
 * @readonly
 * @enum {string}
 */
export const BOX_SYSTEMS = {
  PENTATONIC: 'pentatonic',
  CAGED: 'caged',
  TNPS: 'tnps', // Three Notes Per String
};

/**
 * Available box patterns for each system
 * @type {Object.<string, string[]>}
 */
export const BOX_PATTERNS = {
  [BOX_SYSTEMS.PENTATONIC]: ['1', '2', '3', '4', '5'],
  [BOX_SYSTEMS.CAGED]: ['C', 'A', 'G', 'E', 'D'],
  [BOX_SYSTEMS.TNPS]: ['1', '2', '3', '4', '5', '6', '7'],
};

/**
 * Scale types that support each box system
 * @type {Object.<string, string[]>}
 */
export const SYSTEM_SCALE_COMPATIBILITY = {
  [BOX_SYSTEMS.PENTATONIC]: [
    'major pentatonic',
    'minor pentatonic',
  ],
  [BOX_SYSTEMS.CAGED]: [
    'major',
    'minor',
    'ionian',
    'dorian',
    'phrygian',
    'lydian',
    'mixolydian',
    'aeolian',
    'locrian',
  ],
  [BOX_SYSTEMS.TNPS]: [
    'major',
    'minor',
    'ionian',
    'dorian',
    'phrygian',
    'lydian',
    'mixolydian',
    'aeolian',
    'locrian',
  ],
};

/**
 * Get the appropriate box system for a scale type
 * @param {string} scaleType - The type of scale
 * @returns {string|null} The box system to use, or null if no system is appropriate
 */
export function getBoxSystem(scaleType) {
  if (!scaleType) return null;
  
  // Check each system for compatibility
  for (const [system, scales] of Object.entries(SYSTEM_SCALE_COMPATIBILITY)) {
    if (scales.includes(scaleType.toLowerCase())) {
      return system;
    }
  }
  
  return null;
}

/**
 * Get valid box patterns for a scale type
 * @param {string} scaleType - The type of scale
 * @returns {string[]} Array of valid box patterns, or empty array if none are valid
 */
export function getValidBoxes(scaleType) {
  const system = getBoxSystem(scaleType);
  if (!system) return [];
  
  return ['all', ...BOX_PATTERNS[system]];
}

/**
 * Check if a box pattern is valid for a scale type
 * @param {string} box - The box pattern to check
 * @param {string} scaleType - The type of scale
 * @returns {boolean} True if the box pattern is valid
 */
export function isValidBox(box, scaleType) {
  if (box === 'all') return true;
  
  const validBoxes = getValidBoxes(scaleType);
  return validBoxes.includes(box);
}

/**
 * Creates a scale configuration for the fretboard
 * @param {string} root - Root note of the scale
 * @param {string} mode - Scale mode/type
 * @param {string} box - Box pattern to display
 * @returns {Object} Scale configuration for the fretboard
 */
export function createBoxConfig(root, mode, box) {
  if (!root || !mode || !isValidBox(box, mode)) {
    return null;
  }

  const system = getBoxSystem(mode);
  if (!system) return null;

  // For 'all' box, return the full scale
  if (box === 'all') {
    return {
      root,
      type: mode,
      positions: []
    };
  }

  // Convert box pattern to position number
  let position;
  system === BOX_SYSTEMS.CAGED ? 
    // Convert CAGED pattern to number (C=0, A=1, G=2, E=3, D=4)
    position = ['C', 'A', 'G', 'E', 'D'].indexOf(box) : position = Number.parseInt(box) - 1; // Convert 1-based to 0-based index

  return {
    root,
    type: mode,
    positions: position >= 0 ? [position] : []
  };
}